import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const email = form.email.trim().toLowerCase();
    const password = form.password.trim();
    try {
      if (mode === "login") {
        const u = await login(email, password);
        toast.success(`Welcome back, ${u.name}`);
        navigate(u.role === "admin" ? "/admin" : "/account");
      } else {
        const u = await register(form.name.trim(), email, password);
        toast.success("Your account is ready!");
        navigate("/account");
      }
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-20">
        <div className="text-center mb-8">
          <p className="eyebrow">{mode === "login" ? "Welcome back" : "Join LuvSprout"}</p>
          <h1 className="mt-3 font-serif text-4xl text-charcoal">
            {mode === "login" ? "Sign In" : "Create Account"}
          </h1>
        </div>
        <div className="bg-white border border-soft-border rounded-sm p-8">
          <div className="flex mb-6 border border-soft-border rounded-full p-1">
            {["login", "register"].map((m) => (
              <button
                key={m}
                data-testid={`tab-${m}`}
                onClick={() => { setMode(m); setErr(""); }}
                className={`flex-1 py-2 text-xs font-display uppercase tracking-[0.12em] rounded-full transition-colors ${mode === m ? "bg-charcoal text-white" : "text-charcoal/60"}`}
              >
                {m === "login" ? "Sign In" : "Register"}
              </button>
            ))}
          </div>
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label className="text-xs eyebrow text-charcoal/70">Name</Label>
                <Input data-testid="auth-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11 rounded-none border-charcoal/20" />
              </div>
            )}
            <div>
              <Label className="text-xs eyebrow text-charcoal/70">Email</Label>
              <Input data-testid="auth-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11 rounded-none border-charcoal/20" />
            </div>
            <div>
              <Label className="text-xs eyebrow text-charcoal/70">Password</Label>
              <Input data-testid="auth-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 h-11 rounded-none border-charcoal/20" />
            </div>
            {err && <p className="text-sm text-destructive" data-testid="auth-error">{err}</p>}
            <Button type="submit" data-testid="auth-submit" disabled={busy} className="w-full h-12 bg-charcoal text-white rounded-none hover:bg-gold font-display tracking-wide">
              {busy ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
            </Button>
          </form>
        </div>
        <p className="text-center text-xs text-charcoal/50 mt-6">
          Continue as guest? <Link to="/shop" className="text-gold hover:underline">Keep shopping</Link>
        </p>
      </div>
    </Layout>
  );
}
